import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Compass } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleAuth() {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const nav = useNavigate();
  const { user, login } = useAuth();
  const { branding, version } = useBranding();
  const [email, setEmail] = useState("md@dreamhome.co");
  const [password, setPassword] = useState("Dream@2026");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) nav("/app", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome back to ${branding?.company_name || "Dream Home ERP"}`);
      nav("/app", { replace: true });
    } else {
      setError(res.error);
    }
  };

  const heroBg = branding?.login_bg_url;
  const companyName = branding?.company_name || "Dream Home ERP";
  const tagline = branding?.tagline || "Business Operating System for Architecture & Engineering Companies";

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white" data-testid="login-page">
      <div
        className="relative hidden md:block border-r border-zinc-200 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${branding?.primary_color || "#18181b"}, ${branding?.secondary_color || "#3f3f46"})`,
        }}
      >
        {heroBg && <img src={heroBg} alt="Blueprint" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
        <div className="relative z-10 p-10 flex flex-col h-full text-white">
          <Link to="/" className="flex items-center gap-2.5" data-testid="login-logo">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="logo" className="w-9 h-9 rounded-md object-contain bg-white/10 p-1" />
            ) : (
              <div className="w-9 h-9 rounded-md bg-white/15 grid place-items-center">
                <Compass className="w-4 h-4" />
              </div>
            )}
            <div className="leading-tight">
              <div className="text-[13px] font-heading font-bold tracking-tight">{companyName}</div>
              <div className="text-[10px] opacity-70 uppercase tracking-widest">Business Operating System</div>
            </div>
          </Link>
          <div className="mt-auto">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] opacity-80">Business Operating System</div>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight mt-2 max-w-md">
              {tagline}
            </h2>
            <p className="text-sm opacity-80 mt-4 max-w-md leading-relaxed">
              CRM, quotations, production hours, revenue, HR — one governed platform.
            </p>
            {version?.version && (
              <div className="mt-6 font-mono text-[11px] opacity-70" data-testid="login-version">
                {version.version}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Sign in</h1>
            <p className="text-sm text-zinc-500 mt-1">Access the {companyName} workspace.</p>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="login-password"
              />
            </div>
          </div>
          {error && <div className="text-xs text-red-600" data-testid="login-error">{error}</div>}
          <Button type="submit" className="w-full h-10" disabled={submitting} data-testid="login-submit">
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-zinc-200" /></div>
            <div className="relative flex justify-center text-[10px] uppercase"><span className="bg-white px-2 text-zinc-500 tracking-widest">or</span></div>
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full h-10 gap-2"
            onClick={googleAuth}
            data-testid="google-signin-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.65 4.1-5.5 4.1-3.31 0-6-2.73-6-6.1S8.69 6 12 6c1.88 0 3.15.79 3.87 1.47l2.64-2.55C16.86 3.41 14.66 2.4 12 2.4 6.72 2.4 2.4 6.72 2.4 12s4.32 9.6 9.6 9.6c5.55 0 9.22-3.89 9.22-9.36 0-.63-.07-1.11-.16-1.6H12z"/></svg>
            Continue with Google
          </Button>
          <div className="text-[11px] text-zinc-500 pt-1" data-testid="login-help">
            Employee accounts are provisioned by Administration. Contact your HR or Managing Director if you need access.
          </div>
          <div className="text-[11px] text-zinc-400 border-t border-zinc-100 pt-4 font-mono">
            Demo: md@dreamhome.co / Dream@2026
          </div>
        </form>
      </div>
    </div>
  );
}
